import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  ActionIcon,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Menu,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconCopy,
  IconDots,
  IconDownload,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import {
  DeleteReportConfigModal,
  DuplicateReportConfigModal,
  ImportReportConfigModal,
  LogoPaper,
  PageTitle,
} from '@/components';
import { useReportConfigs, useTeams } from '@/hooks';
import { ConfigExportPayload, ReportConfig, ReportConfigSummary } from '@/types';

/** Rows per grid page (the API caps a page at 200). */
const PAGE_SIZE = 25;

function downloadJsonFile(payload: ConfigExportPayload, filename: string) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function exportFilename(slug: string): string {
  const today = new Date().toISOString().slice(0, 10);
  return `${slug}-${today}.json`;
}

export function ReportConfigsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTeamId = searchParams.get('team_id') ?? undefined;

  const { teams, isLoading: teamsLoading } = useTeams();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [page, setPage] = useState(1);
  const resetPage = () => setPage(1);

  // Grid mode: one server page, searched server-side (name / slug /
  // description) — the same hook the pickers use, plus the page params.
  const { configs, total, isLoading, deleteConfig, duplicateConfig, exportConfig } =
    useReportConfigs({
      teamId: selectedTeamId,
      page,
      pageSize: PAGE_SIZE,
      q: debouncedSearch || undefined,
    });
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const [deleteTarget, setDeleteTarget] = useState<ReportConfigSummary | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<ReportConfigSummary | null>(null);
  const [deleteOpened, deleteHandlers] = useDisclosure(false);
  const [duplicateOpened, duplicateHandlers] = useDisclosure(false);
  const [importOpened, importHandlers] = useDisclosure(false);

  const handleTeamChange = (value: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('team_id', value);
    } else {
      newParams.delete('team_id');
    }
    setSearchParams(newParams);
    resetPage();
  };

  const handleDelete = (config: ReportConfigSummary) => {
    setDeleteTarget(config);
    deleteHandlers.open();
  };

  const handleDuplicate = (config: ReportConfigSummary) => {
    setDuplicateTarget(config);
    duplicateHandlers.open();
  };

  const confirmDelete = async () => {
    if (deleteTarget) {
      await deleteConfig(deleteTarget.id);
      deleteHandlers.close();
      setDeleteTarget(null);
    }
  };

  const confirmDuplicate = async (newName: string, newSlug: string) => {
    if (duplicateTarget) {
      await duplicateConfig(duplicateTarget.id, newName, newSlug);
      duplicateHandlers.close();
      setDuplicateTarget(null);
    }
  };

  const handleExport = async (config: ReportConfigSummary) => {
    try {
      const { payload, filename } = await exportConfig(config.id);
      // Server sets Content-Disposition with the canonical filename;
      // fall back to client synthesis if CORS strips the header.
      const name = filename ?? exportFilename(config.slug);
      downloadJsonFile(payload, name);
      notifications.show({
        title: 'Export ready',
        message: `Downloaded ${name}`,
        color: 'green',
      });
    } catch (err) {
      // exportConfig already toasts the failure; swallow here to keep
      // the menu interaction clean.
      Sentry.captureException(err);
    }
  };

  const handleImported = (created: ReportConfig) => {
    importHandlers.close();
    const params = selectedTeamId ? `?team_id=${selectedTeamId}` : '';
    navigate(`/admin/report-configs/${created.id}${params}`);
  };

  const teamSelectData = teams.map((t) => ({
    value: String(t.id),
    label: t.name,
  }));

  return (
    <Stack gap="xl">
      <PageTitle
        title="Report Configurations"
        subtitle="Manage screening pipeline configurations for clients"
      />

      <Container size="xl" px={0}>
        <LogoPaper>
          <Stack gap="md">
            <Group justify="space-between">
              <Group>
                <Select
                  placeholder="My Team (default)"
                  data={teamSelectData}
                  value={selectedTeamId ?? null}
                  onChange={handleTeamChange}
                  clearable
                  searchable
                  disabled={teamsLoading}
                  w={250}
                />
                <TextInput
                  placeholder="Search configs..."
                  leftSection={<IconSearch size={16} />}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.currentTarget.value);
                    resetPage();
                  }}
                  w={250}
                />
              </Group>
              <Group gap="xs">
                <Button
                  variant="default"
                  leftSection={<IconUpload size={16} />}
                  onClick={importHandlers.open}
                >
                  Import
                </Button>
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={() => {
                    const params = selectedTeamId ? `?team_id=${selectedTeamId}` : '';
                    navigate(`/admin/report-configs/new${params}`);
                  }}
                >
                  Create New
                </Button>
              </Group>
            </Group>

            {isLoading ? (
              <Group justify="center" py="xl">
                <Loader />
              </Group>
            ) : configs.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                {search ? 'No configurations match your search.' : 'No configurations found.'}
              </Text>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Slug</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Components</Table.Th>
                    <Table.Th>Version</Table.Th>
                    <Table.Th w={60} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {configs.map((config) => (
                    <Table.Tr
                      key={config.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const params = selectedTeamId ? `?team_id=${selectedTeamId}` : '';
                        navigate(`/admin/report-configs/${config.id}${params}`);
                      }}
                    >
                      <Table.Td>
                        <Group gap="xs">
                          <Text fw={500}>{config.name}</Text>
                          {config.teamIds.length === 0 && (
                            <Badge
                              size="xs"
                              variant="light"
                              color="blue"
                              title="Applicable to every team"
                            >
                              All teams
                            </Badge>
                          )}
                          {!config.visible && (
                            <Badge
                              size="xs"
                              variant="light"
                              color="gray"
                              title="Admin-only — hidden from client config pickers"
                            >
                              Hidden from clients
                            </Badge>
                          )}
                          {config.approvalRequired === 'enabled' && (
                            <Badge size="xs" variant="light" color="violet">
                              Approval Required
                            </Badge>
                          )}
                          {config.pipelineMode === 'screening_only' && (
                            <Badge
                              size="xs"
                              variant="light"
                              color="teal"
                              title="Basic-screening tier — identification and deep research skipped"
                            >
                              Basic screening
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {config.slug}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={1}>
                          {config.description ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light" color={config.isActive ? 'green' : 'gray'}>
                          {config.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{config.componentsTotal}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">v{config.version}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Menu shadow="md" width={160} position="bottom-end">
                          <Menu.Target>
                            <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                            <Menu.Item
                              leftSection={<IconPencil size={14} />}
                              onClick={() => {
                                const params = selectedTeamId ? `?team_id=${selectedTeamId}` : '';
                                navigate(`/admin/report-configs/${config.id}${params}`);
                              }}
                            >
                              Edit
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconCopy size={14} />}
                              onClick={() => handleDuplicate(config)}
                            >
                              Duplicate
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconDownload size={14} />}
                              onClick={() => handleExport(config)}
                            >
                              Export
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              onClick={() => handleDelete(config)}
                            >
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {totalPages > 1 ? (
              <Group justify="center">
                <Pagination value={page} onChange={setPage} total={totalPages} />
              </Group>
            ) : null}
          </Stack>
        </LogoPaper>
      </Container>

      <DeleteReportConfigModal
        opened={deleteOpened}
        onClose={() => {
          deleteHandlers.close();
          setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
        configName={deleteTarget?.name ?? ''}
      />

      <DuplicateReportConfigModal
        opened={duplicateOpened}
        onClose={() => {
          duplicateHandlers.close();
          setDuplicateTarget(null);
        }}
        onConfirm={confirmDuplicate}
        originalName={duplicateTarget?.name ?? ''}
      />

      <ImportReportConfigModal
        opened={importOpened}
        onClose={importHandlers.close}
        teamId={selectedTeamId}
        onImported={handleImported}
      />
    </Stack>
  );
}
